
import React, { useState, useEffect } from "react";

interface Parameter {
  id: number;
  symbol?: string;
  parameter: string;
  value: string;
}

interface Props {
  data: Parameter[];
  loading: boolean;
  onUpdate: (
    updated: Parameter[],
    index: number,
    field: string
  ) => void;
}

declare global {
  interface Window {
    chrome?: {
      webview?: {
        postMessage: (message: unknown) => void;
        addEventListener: (
          type: "message",
          listener: (event: MessageEvent) => void
        ) => void;
        removeEventListener: (
          type: "message",
          listener: (event: MessageEvent) => void
        ) => void;
      };
    };
  }
}

// AutoCAD-style red A icon.
// Click sends the event to the WinForms WebView2 host.
const AutoCadIcon = () => {
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();

    if (!window.chrome?.webview) {
      return;
    }

    window.chrome.webview.postMessage({
      type: "OPEN_AUTOCAD",
    });
  };

  return (
    <button
      type="button"
      title="AutoCAD"
      aria-label="AutoCAD"
      onClick={handleClick}
      style={{
        width: 22,
        height: 22,
        padding: 1,
        border: "none",
        borderRadius: 2,
        background: "transparent",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg
        viewBox="0 0 32 32"
        width="19"
        height="19"
        aria-hidden="true"
      >
        <path
          d="M16 2 30 27H22L16 15 10 27H2Z"
          fill="#E2231A"
        />
        <path
          d="M12 23H20L16 15Z"
          fill="#FFFFFF"
        />
      </svg>
    </button>
  );
};

const ParametersTable = ({
  data,
  loading,
  onUpdate,
}: Props) => {
  const [editIndex, setEditIndex] =
    useState<number | null>(null);

  const [editField, setEditField] =
    useState<"parameter" | "value" | null>(null);

  const [editValue, setEditValue] = useState<string>("");

  // Enable temporarily for local visual testing.
  // Restore false when you want desktop-only visibility.
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect desktop-ready messages from WebView2.
  useEffect(() => {
    const webview = window.chrome?.webview;

    if (!webview) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "BAEVR_DESKTOP_READY") {
        setIsDesktop(true);
      }
    };

    webview.addEventListener("message", handleMessage);

    webview.postMessage({
      type: "BAEVR_DESKTOP_CHECK",
    });

    return () => {
      webview.removeEventListener("message", handleMessage);
    };
  }, []);

  const startEdit = (
    index: number,
    field: "parameter" | "value",
    value: string
  ) => {
    setEditIndex(index);
    setEditField(field);
    setEditValue(value ?? "");
  };

  const handleSave = (index: number) => {
    if (!editField) return;

    const updated = data.map((item) => ({ ...item }));

    if (editField === "parameter") {
      updated[index].parameter = editValue;
    } else if (editField === "value") {
      updated[index].value = editValue;
    }

    onUpdate(updated, index, editField);

    setEditIndex(null);
    setEditField(null);
  };

  if (loading) {
    return (
      <p
        className="text-gray-400"
        style={{
          fontFamily: "'Segoe UI', sans-serif",
          fontSize: 13,
          fontWeight: 400,
        }}
      >
        Loading...
      </p>
    );
  }

  if (data.length === 0) {
    return (
      <p
        className="text-gray-400"
        style={{
          fontFamily: "'Segoe UI', sans-serif",
          fontSize: 13,
          fontWeight: 400,
        }}
      >
        No parameters found
      </p>
    );
  }

  return (
    <div>
      <table
        className="w-full"
        style={{
          fontFamily: "'Segoe UI', sans-serif",
          fontSize: 13,
          fontWeight: 400,
          borderCollapse: "collapse",
        }}
      >
        <thead className="bg-white border-b border-gray-300">
          <tr>
            <th
              className="text-left px-4 py-2 text-black"
              style={{ fontWeight: 400 }}
            >
              Parameter
            </th>

            <th
              className="text-left px-4 py-2 text-black"
              style={{ fontWeight: 400 }}
            >
              Symbol
            </th>

            <th
              className="text-left px-4 py-2 text-black"
              style={{ fontWeight: 400 }}
            >
              Value
            </th>

            {isDesktop && (
              <th
                className="text-center px-1 py-2"
                style={{
                  fontWeight: 400,
                  width: 30,
                }}
                aria-label="AutoCAD"
              />
            )}
          </tr>
        </thead>

        <tbody>
          {data.map((p, i) => (
            <tr
              key={p.id}
              className="bg-white border-b border-gray-200"
            >
              <td className="px-4 py-1 text-gray-800">
                {p.parameter}
              </td>

              <td className="px-4 py-1 text-gray-800">
                {p.symbol || "-"}
              </td>

              <td
                className="px-4 py-1 text-gray-900 cursor-pointer"
                onClick={() =>
                  startEdit(i, "value", p.value)
                }
              >
                {editIndex === i &&
                editField === "value" ? (
                  <input
                    autoFocus
                    className="border rounded px-2 py-1 w-full"
                    style={{
                      fontFamily: "'Segoe UI', sans-serif",
                      fontSize: 13,
                      fontWeight: 400,
                    }}
                    value={editValue}
                    onChange={(e) =>
                      setEditValue(e.target.value)
                    }
                    onBlur={() => handleSave(i)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSave(i);
                      }

                      if (e.key === "Escape") {
                        setEditIndex(null);
                        setEditField(null);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : p.value !== null &&
                  p.value !== undefined &&
                  p.value !== "" &&
                  !Number.isNaN(Number(p.value)) ? (
                  Number(p.value).toFixed(2)
                ) : (
                  p.value || "-"
                )}
              </td>

              {/* AutoCAD icon beside Value */}
              {isDesktop && (
                <td
                  className="px-1 py-1 text-center"
                  style={{ width: 30 }}
                >
                  <AutoCadIcon />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ParametersTable;
